import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  automationService,
  type Strategy,
  type PositionSizing,
  type ExitRules,
  type StopRule,
  type TrailingStopRule,
} from '@/services/strategyService';
import { scannerApiService } from '@/services/scannerService';
import { Save, X } from 'lucide-react';

interface RuleFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pass existing rule to edit; null = create new */
  editRule?: Strategy | null;
  onSaved: () => void;
}

interface ScannerOption {
  id: string;
  name: string;
}

const STOP_MODES = ['Dollar', 'Percent', 'AtrMultiple', 'RMultiple'] as const;
const SIZING_MODES = ['FixedShares', 'FixedDollar', 'RiskPercent', 'PercentEquity'] as const;

const defaultSizing: PositionSizing = {
  mode: 'RiskPercent',
  value: 2.0,
  accountEquity: 100000,
  maxNotional: 25000,
};

const defaultExits: ExitRules = {
  stopLoss: { mode: 'AtrMultiple', value: 1.5 },
  takeProfit: null,
  trailing: { mode: 'Percent', value: 1.5, activationPercent: 0, distanceReference: 'LastPrice' },
  breakEvenAfterRMultiple: 1.0,
  maxHoldMinutes: 240,
};

export const RuleFormDialog: React.FC<RuleFormDialogProps> = ({
  open,
  onOpenChange,
  editRule,
  onSaved,
}) => {
  const [scanners, setScanners] = useState<ScannerOption[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scannerId, setScannerId] = useState('');
  const [direction, setDirection] = useState<'Long' | 'Short'>('Long');
  const [sizing, setSizing] = useState<PositionSizing>({ ...defaultSizing });
  const [exits, setExits] = useState<ExitRules>({ ...defaultExits });
  const [cooldownSeconds, setCooldownSeconds] = useState(300);
  const [maxConcurrentTrades, setMaxConcurrentTrades] = useState(5);
  const [regularHoursOnly, setRegularHoursOnly] = useState(true);

  // TP toggle (since it's nullable)
  const [tpEnabled, setTpEnabled] = useState(false);
  const [trailingEnabled, setTrailingEnabled] = useState(true);

  useEffect(() => {
    if (!open) return;
    scannerApiService.getScanners().then((list: any[]) => {
      setScanners(list.map(s => ({ id: s.id, name: s.name })));
    }).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (editRule) {
      setName(editRule.name);
      setDescription(editRule.description);
      setScannerId(editRule.scannerId);
      setDirection(editRule.direction);
      setSizing({ ...editRule.sizing });
      setExits({ ...editRule.exits });
      setCooldownSeconds(editRule.cooldownSeconds);
      setMaxConcurrentTrades(editRule.maxConcurrentTrades);
      setRegularHoursOnly(editRule.regularHoursOnly);
      setTpEnabled(editRule.exits.takeProfit != null);
      setTrailingEnabled(editRule.exits.trailing != null);
    } else {
      setName('');
      setDescription('');
      setScannerId('');
      setDirection('Long');
      setSizing({ ...defaultSizing });
      setExits({ ...defaultExits });
      setCooldownSeconds(300);
      setMaxConcurrentTrades(5);
      setRegularHoursOnly(true);
      setTpEnabled(false);
      setTrailingEnabled(true);
    }
    setError(null);
  }, [open, editRule]);

  const handleSave = async () => {
    if (!name.trim()) { setError('Name is required'); return; }
    if (!scannerId) { setError('Scanner is required'); return; }

    setSaving(true);
    setError(null);
    try {
      const payload: Partial<Strategy> = {
        name: name.trim(),
        description: description.trim(),
        scannerId,
        direction,
        sizing,
        exits: {
          ...exits,
          takeProfit: tpEnabled ? (exits.takeProfit ?? { mode: 'RMultiple', value: 2.0 }) : null,
          trailing: trailingEnabled ? (exits.trailing ?? { mode: 'Percent', value: 1.5, activationPercent: 0, distanceReference: 'LastPrice' }) : null,
        },
        cooldownSeconds,
        maxConcurrentTrades,
        regularHoursOnly,
        enabled: editRule?.enabled ?? false,
      };

      if (editRule) {
        await automationService.updateRule(editRule.id, payload);
      } else {
        await automationService.createRule(payload);
      }
      onSaved();
      onOpenChange(false);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const updateStopLoss = (patch: Partial<StopRule>) => {
    setExits(prev => ({
      ...prev,
      stopLoss: { ...(prev.stopLoss ?? { mode: 'AtrMultiple', value: 1.5 }), ...patch } as StopRule,
    }));
  };

  const updateTakeProfit = (patch: Partial<StopRule>) => {
    setExits(prev => ({
      ...prev,
      takeProfit: { ...(prev.takeProfit ?? { mode: 'RMultiple', value: 2.0 }), ...patch } as StopRule,
    }));
  };

  const updateTrailing = (patch: Partial<TrailingStopRule>) => {
    setExits(prev => ({
      ...prev,
      trailing: {
        ...(prev.trailing ?? { mode: 'Percent', value: 1.5, activationPercent: 0, distanceReference: 'LastPrice' }),
        ...patch,
      } as TrailingStopRule,
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editRule ? 'Edit Rule' : 'New Automation Rule'}</DialogTitle>
          <DialogDescription>
            Configure scanner trigger, position sizing, and exit strategy
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {error && (
            <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">{error}</div>
          )}

          {/* ── Basic Info ── */}
          <Section title="Basic">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name" className="col-span-2">
                <input
                  className="input-field"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="My Trading Rule"
                />
              </Field>
              <Field label="Description" className="col-span-2">
                <input
                  className="input-field"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Enter on scanner hit, ATR stop, trailing exit"
                />
              </Field>
              <Field label="Scanner">
                <select
                  className="input-field"
                  value={scannerId}
                  onChange={e => setScannerId(e.target.value)}
                >
                  <option value="">Select scanner...</option>
                  {scanners.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="Direction">
                <select
                  className="input-field"
                  value={direction}
                  onChange={e => setDirection(e.target.value as 'Long' | 'Short')}
                >
                  <option value="Long">Long</option>
                  <option value="Short">Short</option>
                </select>
              </Field>
            </div>
          </Section>

          {/* ── Position Sizing ── */}
          <Section title="Position Sizing">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Sizing Mode">
                <select
                  className="input-field"
                  value={sizing.mode}
                  onChange={e => setSizing(prev => ({ ...prev, mode: e.target.value as any }))}
                >
                  {SIZING_MODES.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </Field>
              <Field label={sizing.mode === 'RiskPercent' ? 'Risk %' : sizing.mode === 'PercentEquity' ? 'Equity %' : 'Value'}>
                <input
                  type="number"
                  className="input-field"
                  value={sizing.value}
                  onChange={e => setSizing(prev => ({ ...prev, value: Number(e.target.value) }))}
                  step={sizing.mode.includes('Percent') ? 0.5 : 1}
                />
              </Field>
              <Field label="Account Equity ($)">
                <input
                  type="number"
                  className="input-field"
                  value={sizing.accountEquity}
                  onChange={e => setSizing(prev => ({ ...prev, accountEquity: Number(e.target.value) }))}
                />
              </Field>
              <Field label="Max Notional ($)">
                <input
                  type="number"
                  className="input-field"
                  value={sizing.maxNotional}
                  onChange={e => setSizing(prev => ({ ...prev, maxNotional: Number(e.target.value) }))}
                />
              </Field>
            </div>
          </Section>

          {/* ── Exit Rules ── */}
          <Section title="Exit Rules">
            <div className="space-y-3">
              {/* Stop Loss */}
              <div className="grid grid-cols-3 gap-3 items-end">
                <Field label="Stop Loss Mode">
                  <select
                    className="input-field"
                    value={exits.stopLoss?.mode ?? 'AtrMultiple'}
                    onChange={e => updateStopLoss({ mode: e.target.value as any })}
                  >
                    {STOP_MODES.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </Field>
                <Field label="Stop Value">
                  <input
                    type="number"
                    className="input-field"
                    value={exits.stopLoss?.value ?? 1.5}
                    onChange={e => updateStopLoss({ value: Number(e.target.value) })}
                    step={0.1}
                  />
                </Field>
                <div />
              </div>

              {/* Take Profit */}
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="checkbox"
                  id="tp-toggle"
                  checked={tpEnabled}
                  onChange={e => setTpEnabled(e.target.checked)}
                  className="rounded border"
                />
                <label htmlFor="tp-toggle" className="text-xs font-medium cursor-pointer">Enable Take Profit</label>
              </div>
              {tpEnabled && (
                <div className="grid grid-cols-3 gap-3 items-end">
                  <Field label="TP Mode">
                    <select
                      className="input-field"
                      value={exits.takeProfit?.mode ?? 'RMultiple'}
                      onChange={e => updateTakeProfit({ mode: e.target.value as any })}
                    >
                      {STOP_MODES.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </Field>
                  <Field label="TP Value">
                    <input
                      type="number"
                      className="input-field"
                      value={exits.takeProfit?.value ?? 2.0}
                      onChange={e => updateTakeProfit({ value: Number(e.target.value) })}
                      step={0.1}
                    />
                  </Field>
                  <div />
                </div>
              )}

              {/* Trailing Stop */}
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="checkbox"
                  id="trail-toggle"
                  checked={trailingEnabled}
                  onChange={e => setTrailingEnabled(e.target.checked)}
                  className="rounded border"
                />
                <label htmlFor="trail-toggle" className="text-xs font-medium cursor-pointer">Enable Trailing Stop</label>
              </div>
              {trailingEnabled && (
                <>
                  <div className="grid grid-cols-3 gap-3 items-end">
                    <Field label="Trail Mode">
                      <select
                        className="input-field"
                        value={exits.trailing?.mode ?? 'Percent'}
                        onChange={e => updateTrailing({ mode: e.target.value as any })}
                      >
                        {STOP_MODES.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </Field>
                    <Field label="Trail Value">
                      <input
                        type="number"
                        className="input-field"
                        value={exits.trailing?.value ?? 1.5}
                        onChange={e => updateTrailing({ value: Number(e.target.value) })}
                        step={0.1}
                      />
                    </Field>
                    <Field label="Activation %">
                      <input
                        type="number"
                        className="input-field"
                        value={exits.trailing?.activationPercent ?? 0}
                        onChange={e => updateTrailing({ activationPercent: Number(e.target.value) })}
                        step={0.5}
                      />
                    </Field>
                  </div>
                  {/* Distance reference is only meaningful for Percent mode — the other
                      modes use a reference-independent distance (Dollar / ATR / R-multiple).
                      Hide it entirely when not Percent so the form doesn't suggest a
                      setting that has no effect. */}
                  {exits.trailing?.mode === 'Percent' && (
                    <div className="grid grid-cols-1 gap-3 mt-2">
                      <Field label="Trail Distance Reference">
                        <select
                          className="input-field"
                          value={exits.trailing?.distanceReference ?? 'LastPrice'}
                          onChange={e => updateTrailing({ distanceReference: e.target.value as any })}
                        >
                          <option value="LastPrice">Last Price (legacy) — tightens on retrace</option>
                          <option value="MaxFavorable">Max Favorable (classic) — % behind the high</option>
                        </select>
                      </Field>
                    </div>
                  )}
                </>
              )}

              {/* Break-even & Time */}
              <div className="grid grid-cols-2 gap-3 mt-2">
                <Field label="Break-Even After R-Multiple">
                  <input
                    type="number"
                    className="input-field"
                    value={exits.breakEvenAfterRMultiple}
                    onChange={e => setExits(prev => ({ ...prev, breakEvenAfterRMultiple: Number(e.target.value) }))}
                    step={0.5}
                  />
                </Field>
                <Field label="Max Hold Minutes (0 = no limit)">
                  <input
                    type="number"
                    className="input-field"
                    value={exits.maxHoldMinutes}
                    onChange={e => setExits(prev => ({ ...prev, maxHoldMinutes: Number(e.target.value) }))}
                    step={30}
                  />
                </Field>
              </div>
            </div>
          </Section>

          {/* ── Controls ── */}
          <Section title="Controls">
            <div className="grid grid-cols-3 gap-3">
              <Field label="Cooldown (seconds)">
                <input
                  type="number"
                  className="input-field"
                  value={cooldownSeconds}
                  onChange={e => setCooldownSeconds(Number(e.target.value))}
                  min={0}
                  step={60}
                />
              </Field>
              <Field label="Max Concurrent Trades">
                <input
                  type="number"
                  className="input-field"
                  value={maxConcurrentTrades}
                  onChange={e => setMaxConcurrentTrades(Number(e.target.value))}
                  min={1}
                  max={50}
                />
              </Field>
              <Field label="">
                <div className="flex items-center gap-2 h-9">
                  <input
                    type="checkbox"
                    id="rth-toggle"
                    checked={regularHoursOnly}
                    onChange={e => setRegularHoursOnly(e.target.checked)}
                    className="rounded border"
                  />
                  <label htmlFor="rth-toggle" className="text-xs font-medium cursor-pointer">Regular Hours Only</label>
                </div>
              </Field>
            </div>
          </Section>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <X className="h-3.5 w-3.5 mr-1" /> Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            <Save className="h-3.5 w-3.5 mr-1" /> {saving ? 'Saving...' : editRule ? 'Update Rule' : 'Create Rule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Small helpers ──

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Card>
    <CardContent className="p-4">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">{title}</div>
      {children}
    </CardContent>
  </Card>
);

const Field: React.FC<{ label: string; className?: string; children: React.ReactNode }> = ({
  label,
  className,
  children,
}) => (
  <div className={className}>
    {label && <label className="text-xs text-muted-foreground block mb-1">{label}</label>}
    {children}
  </div>
);
